import { useTranslation } from 'react-i18next';
import Card from 'shell/components/Card';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import { FileTypes } from 'utils/constants';
import { EvidenceLocatorKinds } from 'utils/gradingEvidence';
import { formatVideoTime, isValidVideoEvidenceRange } from 'utils/videoGrading';
import { EvidenceStatusTag, SpeakerTag } from './video/EvidenceTags';

const formatLocator = (locator, t) => {
    if (!locator) return '';
    if (locator.kind === EvidenceLocatorKinds.PAGE) {
        return t('grading-section.evidence-page', 'Page {{page}}', { page: locator.value });
    }
    if (locator.kind === EvidenceLocatorKinds.SLIDE) {
        return t('grading-section.evidence-slide', 'Slide {{slide}}', { slide: locator.value });
    }
    if (locator.kind === EvidenceLocatorKinds.PARAGRAPHS) {
        const values = locator.value.join(', ');
        return t(
            locator.value.length === 1 ? 'grading-section.evidence-paragraph' : 'grading-section.evidence-paragraphs',
            locator.value.length === 1 ? 'Paragraph {{paragraphs}}' : 'Paragraphs {{paragraphs}}',
            { paragraphs: values }
        );
    }
    return locator.value || '';
};

const EvidenceLocationCard = ({ evidence, onActivate, disabled = false }) => {
    const { t } = useTranslation('assignment');
    const isVideo = evidence.category === FileTypes.VIDEO;
    const hasVideoTime = isVideo && isValidVideoEvidenceRange({ start: evidence.timeStart, end: evidence.timeEnd });
    const time = hasVideoTime ? `${formatVideoTime(evidence.timeStart)}–${formatVideoTime(evidence.timeEnd)}` : '';
    const locator = isVideo
        ? time || t('video-grading.time-unavailable', 'Time unavailable')
        : formatLocator(evidence.locator, t);
    const accessibleLabel =
        !hasVideoTime && evidence.fileName
            ? t('grading-section.open-evidence-file', 'View evidence file {{name}}', { name: evidence.fileName })
            : t('grading-section.open-evidence', 'View evidence {{location}}', {
                  location:
                      locator ||
                      evidence.quote ||
                      evidence.fileName ||
                      t('grading-section.location-unavailable', 'Location unavailable'),
              });
    const activate = () => {
        if (disabled) return;
        onActivate?.(evidence);
    };

    return (
        <Card
            component={'div'}
            role={'button'}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            aria-label={
                isVideo && hasVideoTime
                    ? t('video-grading.play-range', 'Play evidence from {{time}}', { time: locator })
                    : accessibleLabel
            }
            disabled={disabled}
            onClick={activate}
            sx={(theme) => ({
                alignItems: 'flex-start',
                flexDirection: 'column',
                gap: theme.spacing(2.5),
                p: 3,
                borderRadius: '16px',
                boxShadow: 'none',
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.55 : 1,
                '&:hover': disabled
                    ? undefined
                    : { borderColor: theme.palette.ensightful.primary[500], borderWidth: '1px' },
                '&:active': disabled
                    ? undefined
                    : { borderColor: theme.palette.ensightful.primary[700], borderWidth: '1px' },
                '&:focus-visible': {
                    outline: `3px solid ${theme.palette.ensightful.secondary[400]}`,
                    outlineOffset: '2px',
                },
            })}
        >
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} width={'100%'} spacing={2}>
                <Stack direction={'row'} alignItems={'center'} useFlexGap flexWrap={'wrap'} spacing={2}>
                    {locator && (
                        <Text variant={'p2'} color={'primary'}>
                            {locator}
                        </Text>
                    )}
                    {isVideo && <SpeakerTag label={evidence.speakerLabel} />}
                    {evidence.sheetName && <LocationTag>{evidence.sheetName}</LocationTag>}
                    {evidence.fileName && <LocationTag>{evidence.fileName}</LocationTag>}
                </Stack>
                {evidence.status && <EvidenceStatusTag status={evidence.status} />}
            </Stack>
            {evidence.quote && (
                <Text
                    component={'span'}
                    variant={'p3'}
                    sx={(theme) => ({
                        m: 0,
                        pl: 2,
                        width: '100%',
                        textAlign: 'left',
                        borderLeft: `2px solid ${theme.palette.ensightful.neutral[400]}`,
                        fontStyle: 'italic',
                    })}
                >
                    {evidence.quote}
                </Text>
            )}
            {evidence.explanation && (
                <Text component={'span'} variant={'p3'} sx={{ width: '100%', textAlign: 'left' }}>
                    {evidence.explanation}
                </Text>
            )}
        </Card>
    );
};

const LocationTag = ({ children }) => (
    <Text
        variant={'p3'}
        weight={'medium'}
        sx={(theme) => ({ px: 2, py: 1, borderRadius: '40px', background: theme.palette.ensightful.neutral[100] })}
    >
        {children}
    </Text>
);

export default EvidenceLocationCard;
